import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faHourglass } from '@fortawesome/pro-regular-svg-icons';
import styled, { type CSSProperties } from 'styled-components';

import {
  ManagerProgressionTaskSlug,
  PackConfigGroup,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { BottomPortal } from '@sorare/core/src/atoms/layout/BottomPortal';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PullToRefetch } from '@sorare/core/src/atoms/navigation/PullToRefetch';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance';
import { ScarcityBackground } from '@sorare/core/src/components/card/ScarcityBackground';
import { GemsBalance } from '@sorare/core/src/components/commonGame/GemsBalance';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { isSpecialCardSet } from '@sorare/core/src/lib/cardSets';
import { getCommonCardShardsCount } from '@sorare/core/src/lib/currentUser';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import {
  desktopAndAbove,
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { DailyDeals } from 'components/commonCard/BuyPacks/DailyDeals';
import { DailyDealsTile } from 'components/commonCard/BuyPacks/DailyDealsTile';
import { SeriesDailyClaimModule } from 'components/commonCard/BuyPacks/SeriesDailyClaimModule';

import { BuyGemsBlock } from '../BuyGemsBlock';
import { BuyGemsButtonBlock } from '../BuyGemsButtonBlock';
import type {
  CommonPacksPage_marketRoot,
  CommonPacksPage_tokenRoot,
} from './__generated__/index.graphql';
import wntrLogo from './assets/wntr-logo.svg';
import { CardPackGroup } from '../BuyPacks/CardPackGroup';
import { CommonPacksHeader } from '../BuyPacks/CommonPacksHeader';
import { DailyDealsTitle } from '../BuyPacks/DailyDeals/DailyDealsTitle';
import { DailyDealsBlock } from '../BuyPacks/DailyDealsBlock';
import { SeriesCardPackGroup } from '../BuyPacks/SeriesCardPackGroup';
import { SeriesDailyClaimBlock } from '../BuyPacks/SeriesDailyClaimBlock';
import { usePackAssets } from '../PacksTypePage/usePackAssets';
import wntrHeader from './assets/wntr-header.webp';

const END_OF_WNTR_SERIES = new Date('2026-03-21T00:00:00Z');

const Root = styled.div`
  position: relative;
  isolation: isolate;
  container-type: inline-size;
  padding-bottom: calc(var(--unit) * 8);
`;

const Positioned = styled.div`
  position: sticky;
  left: 0;
  right: 0;
  top: var(--current-stack-height);
  height: 0;
  width: 100%;
  z-index: -1;
`;

const BackgroundWrapper = styled.div`
  position: absolute;
  inset: 0;
  isolation: isolate;
  height: calc(var(--100vh) - var(--current-stack-height));
`;

const Balance = styled(Horizontal)`
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    padding: var(--triple-unit);
  }
`;

const Wrapper = styled(Vertical)`
  gap: var(--double-unit);
  @container ${laptopAndAbove} {
    display: grid;
    grid-template-columns: repeat(max(calc(var(--nb-items) / 2), 2), 1fr);
    gap: var(--triple-unit);
  }
  @container ${desktopAndAbove} {
    grid-template-columns: repeat(max(var(--nb-items), 2), 1fr);
  }
`;

const SeriesDailyClaimWrapper = styled(Horizontal)`
  justify-content: flex-end;
  width: calc(100 * var(--vw) - var(--quadruple-unit));
  @container ${tabletAndAbove} {
    width: 100%;
    padding: 0 var(--double-unit);
    position: sticky;
    bottom: calc(var(--unit) * 6);
    z-index: 1;
    height: 0;
  }
  @container ${laptopAndAbove} {
    display: none;
  }
`;

const Header = styled(Horizontal).attrs({ center: true })`
  gap: var(--double-unit);
  padding: var(--double-unit);
  background: url(${wntrHeader}) center center / cover no-repeat;
  border-radius: var(--double-unit);
  width: calc(100% - var(--quadruple-unit));
  box-shadow:
    0 0 8px 0 rgba(7, 243, 208, 0.5),
    0 0 4px 0 rgba(7, 243, 208, 0.25) inset;
  border: 1px solid #07f3d0;
  margin: 0 auto;
  flex-direction: row-reverse;
  @container ${laptopAndAbove} {
    margin: 0;
    width: 100%;
    border: none;
    box-shadow: none;
    background: none;
    display: flex;
    flex-direction: column;
    padding: 0 var(--triple-unit);
  }
`;

const SeriesLogo = styled.img`
  height: 24px;
  @container ${laptopAndAbove} {
    height: 48px;
    max-width: 280px;
    object-fit: contain;
  }
`;

const DesktopLayout = styled(Vertical)`
  @container ${laptopAndAbove} {
    min-height: max(55vh, 540px);
    display: grid;
    grid-template-columns: 1fr 250px;
    gap: var(--triple-unit);
  }
`;

const DesktopRightColumn = styled(Vertical).attrs({ gap: 3 })`
  display: none;
  @container ${laptopAndAbove} {
    display: flex;
    gap: var(--triple-unit);
  }
`;
const HideOnDesktop = styled.div`
  @container ${laptopAndAbove} {
    display: none;
  }
`;

type Props = {
  refetch: () => Promise<unknown>;
  marketRoot: CommonPacksPage_marketRoot;
  tokenRoot?: CommonPacksPage_tokenRoot;
};
export const CommonPacksPage = ({ refetch, marketRoot, tokenRoot }: Props) => {
  const {
    flags: { usePurchasableItemId = false, useEnableSeriesMarket = false },
  } = useFeatureFlags();

  const isSimplifiedNav = useSeriesNav();
  const isTabletOrAbove = useIsTabletAndAbove();
  const isLaptopOrAbove = useIsLaptop();
  const gemsPacks =
    (usePurchasableItemId
      ? tokenRoot?.purchasableItems
      : marketRoot?.gemsPacks) || [];

  const { currentUser } = useCurrentUserContext();
  const openDialog = useOpenDialog();
  const { dailyDealCardPackGroups, marketCardPackGroups } = groupBy(
    marketRoot.cardPackGroups,
    cardPackGroup => {
      if (cardPackGroup.packConfigGroupType === PackConfigGroup.DAILY_DEAL) {
        return 'dailyDealCardPackGroups';
      }
      return 'marketCardPackGroups';
    }
  );
  const dailyDealCardPackGroup = dailyDealCardPackGroups?.at(0);

  const specialSetCardPackGroups = marketCardPackGroups?.filter(group =>
    isSpecialCardSet(group.cardSet?.slug)
  );
  const specialSetCardPackGroupsLength = specialSetCardPackGroups?.length || 0;

  const commonSetCardPackGroups = marketCardPackGroups?.filter(
    group => !isSpecialCardSet(group.cardSet?.slug)
  );

  const { bgImage } = usePackAssets({
    cardPackGroupId: dailyDealCardPackGroup?.id || '',
  });

  const onPackSelect = (_: string, cardPackSlug: string) => {
    openDialog(DialogKey.cardPack, cardPackSlug);
  };

  const dailyClaimTask = marketRoot.tasks
    .filter(isTypeFn('Task'))
    .find(task => task.name === ManagerProgressionTaskSlug.DAILY_CLAIM);

  return (
    <Root>
      <PullToRefetch refetch={refetch}>
        {!!useEnableSeriesMarket && (
          <Positioned>
            <BackgroundWrapper>
              <ScarcityBackground
                rarity={Rarity.common}
                editionName={'winter'}
              />
            </BackgroundWrapper>
          </Positioned>
        )}
        <Vertical gap={3}>
          {!useEnableSeriesMarket && (
            <CommonPacksHeader
              marketRoot={marketRoot}
              refetch={refetch}
              backgroundUrl={bgImage}
              title={<DailyDealsTitle cardPackGroup={dailyDealCardPackGroup} />}
              balances={
                <Horizontal>
                  <EssenceBalance
                    value={
                      getCommonCardShardsCount(
                        currentUser,
                        'common-base-set'
                      ) || 0
                    }
                    rarity={Rarity.common}
                    cardSetSlug="common-base-set"
                  />
                  <GemsBalance
                    balance={currentUser?.gemsBalances[0]?.amount || 0}
                  />
                </Horizontal>
              }
            >
              {!!dailyDealCardPackGroup && (
                <DailyDeals
                  cardPackGroup={dailyDealCardPackGroup}
                  onClickBuy={onPackSelect}
                />
              )}
            </CommonPacksHeader>
          )}
          {!!useEnableSeriesMarket && (
            <>
              <Balance>
                <EssenceBalance
                  value={getCommonCardShardsCount(currentUser, 'winter') || 0}
                  rarity={Rarity.common}
                  cardSetSlug="winter"
                />
                {!isSimplifiedNav && (
                  <EssenceBalance
                    value={
                      getCommonCardShardsCount(
                        currentUser,
                        'common-base-set'
                      ) || 0
                    }
                    rarity={Rarity.common}
                    cardSetSlug="common-base-set"
                  />
                )}
                <GemsBalance
                  balance={currentUser?.gemsBalances[0]?.amount || 0}
                />
              </Balance>
              <Header>
                <Chip color="blue">
                  <FontAwesomeIcon icon={faHourglass} />
                  <TimeLeft time={END_OF_WNTR_SERIES} />
                </Chip>
                <SeriesLogo src={wntrLogo} alt="" />
              </Header>
            </>
          )}
          <Container maxWidth={1320}>
            {useEnableSeriesMarket ? (
              <DesktopLayout>
                <Wrapper
                  gap={3}
                  style={
                    {
                      '--nb-items': specialSetCardPackGroupsLength,
                    } as CSSProperties
                  }
                >
                  {specialSetCardPackGroups?.map(group => (
                    <SeriesCardPackGroup
                      key={group.id}
                      cardPackGroup={group}
                      onClickBuy={onPackSelect}
                      wide={specialSetCardPackGroupsLength < 4}
                    />
                  ))}

                  <HideOnDesktop>
                    <BuyGemsBlock gemsPacks={gemsPacks} />
                  </HideOnDesktop>

                  {!!dailyDealCardPackGroup && !isSimplifiedNav && (
                    <HideOnDesktop>
                      <DailyDealsBlock
                        bgImage={bgImage}
                        title={
                          <DailyDealsTitle
                            cardPackGroup={dailyDealCardPackGroup}
                          />
                        }
                      >
                        <DailyDeals
                          cardPackGroup={dailyDealCardPackGroup}
                          onClickBuy={onPackSelect}
                        />
                      </DailyDealsBlock>
                    </HideOnDesktop>
                  )}
                </Wrapper>
                <DesktopRightColumn>
                  {!!dailyClaimTask && (
                    <SeriesDailyClaimBlock
                      task={dailyClaimTask}
                      refetch={refetch}
                    />
                  )}
                  <BuyGemsButtonBlock />
                  {!!dailyDealCardPackGroup && !isSimplifiedNav && (
                    <DailyDealsTile
                      bgImage={bgImage}
                      cardPackGroup={dailyDealCardPackGroup}
                    />
                  )}
                </DesktopRightColumn>
              </DesktopLayout>
            ) : (
              <Vertical gap={3}>
                {commonSetCardPackGroups?.map(group => (
                  <CardPackGroup
                    key={group.id}
                    cardPackGroup={group}
                    onClickBuy={onPackSelect}
                  />
                ))}
              </Vertical>
            )}
          </Container>
        </Vertical>
      </PullToRefetch>

      <ConditionalWrapper
        wrap={!!useEnableSeriesMarket && !isLaptopOrAbove}
        Wrapper={isTabletOrAbove ? BottomPortal : BottomPortal.Above}
      >
        <SeriesDailyClaimWrapper>
          {!!dailyClaimTask && (
            <SeriesDailyClaimModule task={dailyClaimTask} refetch={refetch} />
          )}
        </SeriesDailyClaimWrapper>
      </ConditionalWrapper>
    </Root>
  );
};

CommonPacksPage.fragments = {
  marketRoot: gql`
    fragment CommonPacksPage_marketRoot on MarketRoot {
      id
      tasks(sport: $sport) {
        id
        name
        ...SeriesDailyClaimModule_task
        ...SeriesDailyClaimBlock_task
      }
      cardPackGroups(sport: $sport) {
        id
        packConfigGroupType
        cardSet {
          slug
        }
        cardPacks {
          slug
        }
        ...SeriesCardPackGroup_cardPackGroup
        ...CardPackGroup_cardPackGroup
        ...DailyDeals_cardPackGroup
        ...DailyDealsTile_cardPackGroup
      }
      gemsPacks: inGameCurrencyPacks(sport: $sport, currency: COMMON_GEM) {
        id
        ...BuyGemsBlock_inGameCurrencyPack
      }
      ...CommonPacksHeader_marketRoot
    }
    ${CommonPacksHeader.fragments.marketRoot}
    ${DailyDeals.fragments.cardPackGroup}
    ${CardPackGroup.fragments.cardPackGroup}
    ${SeriesCardPackGroup.fragments.cardPackGroup}
    ${BuyGemsBlock.fragments.inGameCurrencyPack}
    ${SeriesDailyClaimModule.fragments.task}
    ${SeriesDailyClaimBlock.fragments.task}
    ${DailyDealsTile.fragments.cardPackGroup}
  ` as TypedDocumentNode<CommonPacksPage_marketRoot>,
  tokenRoot: gql`
    fragment CommonPacksPage_tokenRoot on TokenRoot {
      purchasableItems(sport: FOOTBALL) {
        slug
        ...BuyGemsBlock_inGameCurrencyPurchasableItem
      }
    }
    ${BuyGemsBlock.fragments.inGameCurrencyPurchasableItem}
  ` as TypedDocumentNode<CommonPacksPage_tokenRoot>,
};
